import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { Key } from 'react';
import type { TableProps } from 'antd/es/table';
import { calculateContainerHeight } from 'utils/helperMethods';
import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { CALENDAR_TABLE_HEIGHT_OFFSET } from '../../config/calendarConstants';
import { TPO_METRIC_GRANULARITY_MODAL } from '../../config/testIds';
import ReportExportModal from '../../ReportExport/ReportExportModal';
import { buildPlanMetricExportPayload } from '../../ReportExport/utils/exportPayloadBuilders';
import type { ExportScope } from '../../ReportExport/types';
import { mergeAttributeFilters } from './utils/mergeAttributeFilters';
import usePlanMetricData from './hooks/queries/usePlanMetricData';
import useMetricSort from './hooks/ui/useMetricSort';
import { usePlanMetricColumns } from './hooks/ui/useMetricColumns';
import MetricTable from './MetricTable';
import PlanDetailsFooter from '../components/PlanDetailsFooter';
import ViewSelectModal from '../components/ViewSelectModal';
import TrendChartPanel from './components/TrendChartPanel';
import type {
    AttributeOption,
    FiltersPayload,
    Granularity,
    PlanMetricDimensionRow,
    PlanMetricRow,
    SubMode,
    TimePeriod,
} from './types';

const EMPTY_FILTERS: FiltersPayload = {};

interface PlanMetricViewProps {
    planId: string;
    subMode: SubMode;
    dimension: string;
    attributes: AttributeOption[];
    kpis: string[];
    granularity: Granularity;
    setGranularity: (g: Granularity) => void;
    timePeriod: TimePeriod;
    searchKeyword: string;
    showTrendChart: boolean;
    showExportModal: boolean;
    onCloseExportModal: () => void;
}

const PlanMetricView: FC<PlanMetricViewProps> = ({
    planId,
    subMode,
    dimension,
    attributes,
    kpis,
    granularity,
    setGranularity,
    timePeriod,
    searchKeyword,
    showTrendChart,
    showExportModal,
    onCloseExportModal,
}) => {
    const { currentPlanDetails } = usePlanDetailsContext();
    const { kpiCatalog } = useCustomerConfig();
    const { metricPageConfig } = useTpoGenConfig();
    const { sortBy, sortOrder, handleTableChange, getColumnSortOrder, resetSort } = useMetricSort('');
    const [showGranularityModal, setShowGranularityModal] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    useEffect(() => {
        resetSort();
    }, [granularity, resetSort]);

    // Selection becomes stale when the data shape changes (dimension, KPI,
    // granularity, time period) or when trend mode is closed entirely.
    const primaryKpi = kpis[0];
    useEffect(() => {
        setSelectedRowKeys([]);
    }, [dimension, primaryKpi, granularity, timePeriod, showTrendChart]);

    const data = usePlanMetricData({
        planId,
        subMode,
        dimension,
        attributes,
        kpis,
        granularity,
        timePeriod,
        searchKeyword,
        sharedFilters: EMPTY_FILTERS,
        sortBy,
        sortOrder,
    });

    const handleClickGranularity = useCallback(() => setShowGranularityModal(true), []);

    const columns = usePlanMetricColumns({
        subMode,
        dimension,
        periods: data.periods,
        granularity,
        getColumnSortOrder,
        onClickGranularity: handleClickGranularity,
    });

    const handleApplyGranularity = useCallback(
        (g: Granularity) => {
            setGranularity(g);
            setShowGranularityModal(false);
        },
        [setGranularity],
    );

    const handleSelectionChange = useCallback((keys: Key[]) => {
        setSelectedRowKeys(keys);
    }, []);

    const allKpiKeys = useMemo(() => kpiCatalog.map((kpi) => kpi.key), [kpiCatalog]);

    // Repeats the merge `usePlanMetricData` performs, so a `current_view` export
    // sends the same `filters` as the grid it is exporting.
    const buildPlanMetricPayload = useCallback(
        (scope: ExportScope) =>
            buildPlanMetricExportPayload({
                scope,
                viewType: subMode,
                dimension,
                kpis,
                allKpiKeys,
                granularity,
                timePeriod,
                filters:
                    subMode === 'metric'
                        ? mergeAttributeFilters(
                              EMPTY_FILTERS,
                              metricPageConfig.dimensionKeyMap[dimension] ?? null,
                              attributes,
                          )
                        : EMPTY_FILTERS,
                searchString: searchKeyword,
                sortBy,
                sortingOrder: sortOrder,
                totalCount: data.totalCount,
            }),
        [
            subMode,
            dimension,
            kpis,
            allKpiKeys,
            granularity,
            timePeriod,
            attributes,
            metricPageConfig,
            searchKeyword,
            sortBy,
            sortOrder,
            data.totalCount,
        ],
    );

    const rowSelection = useMemo<TableProps<PlanMetricRow>['rowSelection'] | undefined>(
        () =>
            showTrendChart
                ? {
                      type: 'checkbox',
                      selectedRowKeys,
                      onChange: handleSelectionChange,
                      preserveSelectedRowKeys: true,
                  }
                : undefined,
        [showTrendChart, selectedRowKeys, handleSelectionChange],
    );

    // Trend chart layout: both the table and the chart canvas use the same
    // scaling formula as KPIEvolutionChart (calculateContainerHeight(3)) so
    // they stay proportional across viewport sizes. When the trend chart is
    // closed the table reclaims its full available height.
    const chartHeight = showTrendChart ? calculateContainerHeight(2) : 0;
    const tableHeight = showTrendChart
        ? calculateContainerHeight(2)
        : window.innerHeight - CALENDAR_TABLE_HEIGHT_OFFSET;

    const needsAttribute = subMode === 'metric' && attributes.length === 0;

    return (
        <>
            <MetricTable<PlanMetricRow>
                dataSource={data.dataSource}
                columns={columns}
                isPending={data.isPending}
                isFetching={data.isFetching}
                isFetchingNextPage={data.isFetchingNextPage}
                hasNextPage={data.hasNextPage}
                fetchNextPage={data.fetchNextPage}
                handleTableChange={handleTableChange}
                emptyDescription={
                    needsAttribute ? 'Select an Attribute to load metric data' : undefined
                }
                emptySubtitle={
                    needsAttribute ? 'Choose an Attribute in the toolbar above' : undefined
                }
                rowSelection={rowSelection}
                height={tableHeight}
            />
            {showTrendChart && (
                <TrendChartPanel
                    dataSource={data.dataSource as PlanMetricDimensionRow[]}
                    periods={data.periods}
                    kpiKey={primaryKpi}
                    granularity={granularity}
                    selectedRowKeys={selectedRowKeys}
                    chartBounds={data.chartBounds}
                    height={chartHeight}
                />
            )}
            <PlanDetailsFooter
                loadedCount={data.loadedCount}
                totalCount={data.totalCount}
                createdBy={currentPlanDetails?.created_by}
            />
            <ReportExportModal
                open={showExportModal}
                onClose={onCloseExportModal}
                planId={planId}
                reportKind="plan_metric"
                buildPayload={buildPlanMetricPayload}
            />
            <ViewSelectModal<Granularity>
                open={showGranularityModal}
                title="Calendar View"
                label="Choose Calendar view"
                options={metricPageConfig.granularityOptions}
                initialValue={granularity}
                onCancel={() => setShowGranularityModal(false)}
                onApply={handleApplyGranularity}
                testId={TPO_METRIC_GRANULARITY_MODAL}
            />
        </>
    );
};

PlanMetricView.displayName = 'PlanMetricView';
export default PlanMetricView;
